/**
 * "What is this about?" — link a form to ONE loan (and a drilldown tab) or ONE
 * screen. Re-exported from FormPrimitives as `FormLinkTargetField` /
 * `FormLinkTargetCard`; FORM_STYLE_GUIDE.md §5 has the rules.
 *
 * Two states:
 *   1. EMPTY — a search box (magnifier, label-above). Focused with nothing
 *      typed it offers "Recent loans" (the header Loan Search's recents) and
 *      "Screens". Typing filters both and searches loans on the server
 *      (`GET /underwriting/loan_search`, 250ms debounce, 2+ chars, top 8).
 *      Text starting with http(s):// is a LINK: it's parsed for
 *      /(loan|acquisition)/{24-hex _id}/{tab?} — or a known screen path — and
 *      resolved straight away (no dropdown). A link that doesn't resolve shows
 *      a red message and keeps the text so it can be fixed.
 *   2. LINKED — the field is replaced by a card: icon, the loan in one line
 *      (or the screen), then stage · tab dropdown · Open ↗ (URL in its
 *      tooltip), and ✕ to go back to an empty, focused search box.
 *
 * The value saves IDS, not text — `{ kind: "loan", loanId, loanType, tab }` or
 * `{ kind: "screen", screen }` — plus `info`, a display snapshot (loan number,
 * sync id, address, stage) so the card can render without a refetch. The URL is
 * always BUILT from type + id + tab (`linkTargetUrl`), never kept as pasted
 * text, so pasted query strings can't leak in. Loans are keyed by `_id` only.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CloseRounded from "@mui/icons-material/CloseRounded";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import WebAssetOutlined from "@mui/icons-material/WebAssetOutlined";
import { useSnackbar } from "notistack";

import Txt from "../typography/Txt";
import InputWrapper from "../inputs/shared/InputWrapper";
import { losPipelineApi } from "../../api/losPipelineApi";
import { formatAddressObject } from "../../_src_shared/utils/functions/formatAddressObject";
import { nsKey } from "../../utils/storage/partnerStorage";
import { FIELD_TEXT, FORM, formSx, px } from "../../theme/formStyles";

// The header Loan Search keeps the loans a user opened from it here.
const RECENT_KEY = nsKey("loanRecentSearches");

const ID_RE = /^[a-f0-9]{24}$/i;
const LOAN_LINK_RE = /\/(loan|acquisition)\/([a-f0-9]{24})(?:\/([^?#\s/]*))?/i;

/** /loan/{id}/{tab} — built from the value, never from pasted text. */
export const linkTargetUrl = (v) => {
  if (!v) return null;
  if (v.kind === "screen") return v.screen || null;
  if (v.kind !== "loan" || !ID_RE.test(v.loanId || "")) return null;
  return `/${v.loanType === "acquisition" ? "acquisition" : "loan"}/${v.loanId}${
    v.tab ? `/${v.tab}` : ""
  }`;
};

const stageOf = (loanType) => (loanType === "acquisition" ? "Acquisition" : "Pre-Close");

/** "Loan #2713 · EV31033 — 2151 Bridge View Ct, …" */
export const linkTargetTitle = (v) => {
  if (!v) return "";
  if (v.kind === "screen") return v.info?.label || v.screen;
  if (v.kind === "text") return v.text;
  const i = v.info || {};
  const ids = [i.loanNumber != null ? `#${i.loanNumber}` : null, i.syncId].filter(Boolean).join(" · ");
  const head = `Loan ${ids || `…${String(v.loanId).slice(-6)}`}`;
  if (i.unavailable) return `${head} (unavailable)`;
  return i.address ? `${head} — ${i.address}` : head;
};

/** "#2713 · Fees" — the short line on a board card. */
export const linkTargetShort = (v, tabs = []) => {
  if (!v) return "";
  if (v.kind === "screen") return v.info?.label || v.screen;
  if (v.kind === "text") return v.text;
  const num = v.info?.loanNumber != null ? `#${v.info.loanNumber}` : `…${String(v.loanId).slice(-6)}`;
  const tab = tabs.find((t) => t.path === v.tab)?.label;
  return tab ? `${num} · ${tab}` : num;
};

const loanTypeFromLink = (link) => (/\/acquisition\//i.test(link || "") ? "acquisition" : "loan");

const readRecents = () => {
  try {
    const rows = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(rows) ? rows.filter((r) => ID_RE.test(r?._id || "")) : [];
  } catch (e) {
    return [];
  }
};

/** A search / recent row → the loan option the list renders. */
const toLoanOption = (r) => {
  const loanType = loanTypeFromLink(r.link) === "acquisition" || r.pipeline === "Acquisitions"
    ? "acquisition"
    : "loan";
  return {
    kind: "loan",
    loanId: r._id,
    loanType,
    info: {
      loanNumber: r.loanNumber,
      syncId: r.sync_loan_id,
      loanName: r.loanName,
      address: r.addresses?.[0],
      stage: stageOf(loanType),
    },
  };
};

const optionMatches = (o, q) => {
  const hay =
    o.kind === "screen"
      ? o.info.label
      : [o.info.loanNumber, o.info.syncId, o.info.loanName, o.info.address].filter(Boolean).join(" ");
  return String(hay).toLowerCase().includes(q.toLowerCase());
};

/* ───────────────────────────── the linked card ───────────────────────────── */

export const LinkTargetCard = ({ value, tabsFor, onTabChange, onClear, readOnly }) => {
  const url = linkTargetUrl(value);
  const unavailable = value.kind === "loan" && value.info?.unavailable;
  const tabs = value.kind === "loan" ? tabsFor?.(value.loanType) || [] : [];
  const Icon = value.kind === "screen" ? WebAssetOutlined : HomeOutlined;
  const title = linkTargetTitle(value);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: px(FORM.s.s3),
        px: px(FORM.s.s3),
        py: px(FORM.s.s2 + 2),
        border: `1px solid ${FORM.color.divider}`,
        borderRadius: px(FORM.radius.control),
        bgcolor: FORM.color.surface2,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: px(FORM.radius.control),
          bgcolor: FORM.color.surface,
          border: `1px solid ${FORM.color.divider}`,
          color: FORM.color.ink2,
          "& svg": { fontSize: 17 },
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip title={title} disableInteractive placement="top-start">
          <Txt
            component="div"
            sx={{
              fontSize: FORM.type.body[0],
              fontWeight: 600,
              color: unavailable ? FORM.color.ink3 : FORM.color.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Txt>
        </Tooltip>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: px(6), mt: px(2), ...formSx.meta }}>
          {value.kind === "screen" ? (
            <span>Screen, no specific loan</span>
          ) : (
            <>
              <span>{value.info?.stage || stageOf(value.loanType)}</span>
              {!readOnly && !unavailable && tabs.length ? (
                <>
                  <span aria-hidden>·</span>
                  <Select
                    variant="standard"
                    disableUnderline
                    value={tabs.some((t) => t.path === value.tab) ? value.tab : "summary"}
                    onChange={(e) => onTabChange?.(e.target.value)}
                    inputProps={{ "aria-label": "Drilldown tab" }}
                    sx={{
                      fontSize: FORM.type.meta[0],
                      color: FORM.color.ink2,
                      fontWeight: 600,
                      "& .MuiSelect-select": { py: 0, pr: "20px !important" },
                      "& .MuiSvgIcon-root": { fontSize: 16, right: 0 },
                    }}
                  >
                    {tabs.map((t) => (
                      <MenuItem key={t.path} value={t.path} sx={{ fontSize: 13 }}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              ) : value.tab ? (
                <>
                  <span aria-hidden>·</span>
                  <span>{tabs.find((t) => t.path === value.tab)?.label || value.tab}</span>
                </>
              ) : null}
            </>
          )}
          {url && !unavailable ? (
            <>
              <span aria-hidden>·</span>
              <Tooltip title={`${window.location.origin}${url}`} disableInteractive>
                <Box
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: px(2),
                    color: FORM.color.accent,
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                    "& svg": { fontSize: 13 },
                  }}
                >
                  Open <OpenInNewRounded />
                </Box>
              </Tooltip>
            </>
          ) : null}
        </Box>
      </Box>
      {!readOnly ? (
        <Tooltip title="Remove link" disableInteractive>
          <IconButton size="small" onClick={onClear} aria-label="Remove link">
            <CloseRounded sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

LinkTargetCard.propTypes = {
  value: PropTypes.object.isRequired,
  tabsFor: PropTypes.func,
  onTabChange: PropTypes.func,
  onClear: PropTypes.func,
  readOnly: PropTypes.bool,
};

/* ───────────────────────────── the field ───────────────────────────── */

const fullLoanInfo = (loan, loanType) => ({
  loanNumber: loan?.loanNumber,
  syncId: loan?.sync_loan_id,
  loanName: loan?.loanName,
  address: loan?.subjectProperties?.[0]?.address
    ? formatAddressObject(loan.subjectProperties[0].address)
    : undefined,
  stage: stageOf(loanType),
});

export const LinkTargetField = ({
  label = "Loan or screen",
  hint = "optional · search or paste a link",
  value,
  onChange,
  screens = [],
  tabsFor,
  placeholder = "Loan #, address, screen name — or paste a link",
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef(null);
  // Free text (anything that isn't picked or a resolvable link) is kept as
  // `{ kind: "text", text }`, so it stays in the box and is saved.
  const [input, setInput] = useState(value?.kind === "text" ? value.text : "");
  const linked = !!value && value.kind !== "text";
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");
  const [focusAfterClear, setFocusAfterClear] = useState(false);
  const recents = useMemo(() => readRecents().slice(0, 5).map(toLoanOption), []);

  const q = input.trim();
  const isUrl = /^https?:\/\//i.test(q);

  const screenOptions = useMemo(
    () => screens.map((s) => ({ kind: "screen", screen: s.path, info: { label: s.label } })),
    [screens],
  );

  // Server search for loans once 2+ characters are typed (not for a URL).
  useEffect(() => {
    if (q.length < 2 || isUrl) {
      setLoans([]);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      losPipelineApi
        .searchLoans({ q, limit: 8, signal: controller.signal })
        .then((rows) => setLoans(rows.filter((r) => ID_RE.test(r?._id || "")).map(toLoanOption)))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [q, isUrl]);

  // After ✕, the search box comes back with the cursor already in it.
  useEffect(() => {
    if (!value && focusAfterClear) {
      inputRef.current?.focus();
      setFocusAfterClear(false);
    }
  }, [value, focusAfterClear]);

  const options = useMemo(() => {
    if (isUrl) return [];
    if (!q) {
      return [
        ...recents.map((o) => ({ ...o, group: "Recent loans" })),
        ...screenOptions.map((o) => ({ ...o, group: "Screens" })),
      ];
    }
    const seen = new Set();
    const loanHits = [...recents.filter((o) => optionMatches(o, q)), ...loans].filter((o) => {
      if (seen.has(o.loanId)) return false;
      seen.add(o.loanId);
      return true;
    });
    return [
      ...loanHits.slice(0, 10).map((o) => ({ ...o, group: "Loans" })),
      ...screenOptions.filter((o) => optionMatches(o, q)).map((o) => ({ ...o, group: "Screens" })),
    ];
  }, [q, isUrl, recents, loans, screenOptions]);

  /** Resolve a pasted / typed URL. Unknown tab → Summary. */
  const resolveUrl = async (text) => {
    setError("");
    const m = LOAN_LINK_RE.exec(text);
    if (m) {
      const loanType = m[1].toLowerCase() === "acquisition" ? "acquisition" : "loan";
      const loanId = m[2];
      const tabs = tabsFor?.(loanType) || [];
      const tab = tabs.some((t) => t.path === m[3]) ? m[3] : "summary";
      const recent = recents.find((o) => o.loanId === loanId);
      let info = recent?.info;
      if (!info) {
        setResolving(true);
        try {
          const loan =
            loanType === "acquisition"
              ? await losPipelineApi.getAcquisitionLoan(loanId)
              : await losPipelineApi.getLoan(loanId);
          info = loan?._id || loan?.loanNumber != null ? fullLoanInfo(loan, loanType) : null;
        } catch (e) {
          info = null;
        } finally {
          setResolving(false);
        }
      }
      if (!info) {
        setError("That link didn't match a loan. Check it, or search instead.");
        return;
      }
      onChange({ kind: "loan", loanId, loanType, tab, info });
      setInput("");
      enqueueSnackbar("Linked from URL", { variant: "success" });
      return;
    }
    let path = "";
    try {
      path = new URL(text).pathname.replace(/\/+$/, "");
    } catch (e) {
      path = "";
    }
    const screen = screens.find((s) => path === s.path || path.startsWith(`${s.path}/`));
    if (screen) {
      onChange({ kind: "screen", screen: screen.path, info: { label: screen.label } });
      setInput("");
      enqueueSnackbar("Linked from URL", { variant: "success" });
      return;
    }
    setError("That link didn't match a loan. Check it, or search instead.");
  };

  const field = (
    <InputWrapper
      label={
        <>
          {label}
          {hint ? (
            <Box component="span" sx={formSx.labelOptional}>
              · {hint}
            </Box>
          ) : null}
        </>
      }
      labelSx={FIELD_TEXT.label}
      helperText={error || undefined}
      helperSx={{ ...FIELD_TEXT.helper, ...(error && { color: FORM.color.required, fontStyle: "normal" }) }}
    >
      {linked ? (
        <LinkTargetCard
          value={value}
          tabsFor={tabsFor}
          onTabChange={(tab) => onChange({ ...value, tab })}
          onClear={() => {
            onChange(null);
            setFocusAfterClear(true);
          }}
        />
      ) : (
        <Autocomplete
          size="small"
          fullWidth
          openOnFocus
          freeSolo
          autoHighlight
          options={options}
          value={null}
          loading={loading || resolving}
          open={isUrl ? false : undefined}
          filterOptions={(x) => x}
          groupBy={(o) => o.group}
          inputValue={input}
          onInputChange={(_, v, reason) => {
            if (reason === "reset") return;
            setInput(v);
            setError("");
            // Keep whatever is typed; a URL is also tried as a link.
            onChange(v.trim() ? { kind: "text", text: v } : null);
            if (/^https?:\/\//i.test(v.trim())) resolveUrl(v.trim());
          }}
          onChange={(_, next) => {
            // Enter on free text: keep it (a URL gets another try as a link).
            if (typeof next === "string") {
              if (/^https?:\/\//i.test(next.trim())) resolveUrl(next.trim());
              return;
            }
            if (!next) return;
            const { group, ...picked } = next;
            onChange(picked.kind === "loan" ? { ...picked, tab: "summary" } : picked);
            setInput("");
          }}
          getOptionLabel={(o) => (typeof o === "string" ? o : linkTargetTitle(o))}
          isOptionEqualToValue={() => false}
          noOptionsText={q.length === 1 ? "Keep typing…" : "No matches"}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              placeholder={placeholder}
              sx={formSx.input}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <SearchRounded sx={{ fontSize: 18, color: FORM.color.ink4, ml: px(2), mr: px(2) }} />
                ),
              }}
            />
          )}
          renderOption={(props, o) => (
            <Box
              component="li"
              {...props}
              key={o.kind === "screen" ? `s:${o.screen}` : `${o.group}:${o.loanId}`}
              sx={{ display: "flex !important", alignItems: "center", gap: 1.5 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Txt component="div" sx={{ fontSize: FORM.type.body[0], color: FORM.color.ink }}>
                  {o.kind === "screen"
                    ? o.info.label
                    : [o.info.loanNumber != null ? `#${o.info.loanNumber}` : null, o.info.syncId || o.info.loanName]
                        .filter(Boolean)
                        .join(" · ")}
                </Txt>
                {o.kind === "loan" && o.info.address ? (
                  <Txt
                    component="div"
                    sx={{ ...formSx.meta, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {o.info.address}
                  </Txt>
                ) : null}
              </Box>
              {o.kind === "loan" ? (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    px: 1,
                    py: "1px",
                    borderRadius: "6px",
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: FORM.color.surface3,
                    color: FORM.color.ink2,
                  }}
                >
                  {o.info.stage}
                </Box>
              ) : null}
            </Box>
          )}
          slotProps={{ paper: { sx: { fontSize: FORM.type.body[0] } } }}
        />
      )}
    </InputWrapper>
  );

  return field;
};

LinkTargetField.propTypes = {
  label: PropTypes.node,
  hint: PropTypes.node,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  /** `{ path, label }` screens — offered on focus, matched by name and by URL. */
  screens: PropTypes.array,
  /** loanType → `{ path, label }` drilldown tabs that exist for that kind of loan. */
  tabsFor: PropTypes.func,
  placeholder: PropTypes.string,
};

export default LinkTargetField;
